import type { ClientBankAccountsLists, PaasTransaction, ClientBalanceResponse, VerifyInteracRequest } from 'vopay';

import { useApi } from '#shared/providers/Api';

export type VerificationResponse = {
  status:
    | 'VERIFICATION_UNAVAILABLE'
    | 'VERIFICATION_SEND_SUCCESS'
    | 'VERIFICATION_LINK_SUCCESS'
    | 'VERIFICATION_FAIL'
    | 'VERIFICATION_ELINX_SETTINGS_UNAVAILABLE';
  message?: string;
  link?: string;
};

export type TransactionData = {
  transactions: PaasTransaction[];
  lastRetrievedTime: string;
};

export type BalanceData = {
  balance: ClientBalanceResponse['AccountBalance'];
  asOfDate: ClientBalanceResponse['BalanceAsAtDate'];
};

export type BankAccountDetails = {
  account: ClientBankAccountsLists;
  balanceData: BalanceData;
  transactionData: TransactionData | null;
};

type HookProps = {
  accountID: string;
  email: string;
  // Host facts the portal read from its ClientData/Permissions/LockedFeatures
  // providers; shared takes them as options instead.
  loginUserName?: string;
  clientAccountsEnabled?: boolean;
  hasViewBankAccountTransactionHistoryPermission?: boolean;
  hasViewElinxSettingsPermission?: boolean;
  isPayLinkEnabled?: boolean;
};

export default function usePaymentMethodDetails({
  accountID,
  email,
  loginUserName = '',
  clientAccountsEnabled = false,
  hasViewBankAccountTransactionHistoryPermission = false,
  hasViewElinxSettingsPermission = false,
  isPayLinkEnabled = false,
}: HookProps) {
  const api = useApi();

  async function fetchBankAccountDetails(token: string): Promise<BankAccountDetails> {
    const [account, balanceData, transactionData] = await Promise.all([
      fetchBankAccount(token),
      hasViewBankAccountTransactionHistoryPermission
        ? fetchBalanceData(token)
        : Promise.resolve({ balance: '', asOfDate: '' }),
      hasViewBankAccountTransactionHistoryPermission ? fetchTransactionData(token) : Promise.resolve(null),
    ]);

    return {
      account,
      balanceData,
      transactionData,
    };
  }

  async function fetchBankAccount(token: string) {
    const res = await api.fetchClientBankAccounts({
      PortalUser: loginUserName,
      Token: token,
      ClientAccountID: accountID,
    });

    return res.BankAccounts[0];
  }

  async function fetchTransactionData(Token: string) {
    const res = await api.fetchClientTransactions({
      PortalUser: loginUserName,
      Token,
    });

    if (res.Success && res.AggregatorTransactions.length > 0) {
      return {
        transactions: res.AggregatorTransactions,
        lastRetrievedTime: res.LastRetrievedTime,
      };
    }

    return null;
  }

  async function fetchBalanceData(Token: string): Promise<BalanceData> {
    const res = await api.fetchClientBalance({
      PortalUser: loginUserName,
      Token,
    });

    return {
      balance: res.AccountBalance,
      asOfDate: res.BalanceAsAtDate,
    };
  }

  const verifyInteracAccount = async (interacData: VerifyInteracRequest) => {
    try {
      const basePayload = {
        EmailAddress: interacData.EmailAddress,
        VerificationTier: interacData.VerificationTier,
      };

      const payload = clientAccountsEnabled ? { ClientAccountID: accountID } : { ContactID: interacData.ContactID };

      const verificationResponse = await api.verifyInterac({ ...payload, ...basePayload });

      if (!verificationResponse.Success) {
        throw new Error(verificationResponse.ErrorMessage);
      }

      return verificationResponse;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const verifyCardAccount = async (cardToken: string, verificationTier: 'basic' | 'enhanced') => {
    try {
      const verificationResponse = await api.verifyCard({
        CardToken: cardToken,
        VerificationTier: verificationTier,
      });

      if (!verificationResponse.Success) {
        throw new Error(verificationResponse.ErrorMessage);
      }

      return verificationResponse;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const verifyBankAccount = async (bankAccount: ClientBankAccountsLists): Promise<VerificationResponse> => {
    if (!hasViewElinxSettingsPermission || !isPayLinkEnabled) {
      return {
        status: 'VERIFICATION_ELINX_SETTINGS_UNAVAILABLE',
      };
    }

    const paylinkSettingsResponse = await api.fetchPaylinkSettings({
      PortalUser: loginUserName,
    });

    const senderEmail = paylinkSettingsResponse.ELinxSettings?.ELinxSenderEmailAddress;

    if (!senderEmail) {
      return {
        status: 'VERIFICATION_UNAVAILABLE',
      };
    }

    const shouldSendEmail = senderEmail !== '' && email !== '';

    const verificationResponse = await api.initiateBankAccountVerification({
      Country: bankAccount.Currency === 'CAD' ? 'CA' : 'US',
      VerificationMode: 'credit',
      FirstName:
        bankAccount.AccountHolderName?.split(' ').length >= 2 ? bankAccount.AccountHolderName?.split(' ')[0] : '',
      LastName:
        bankAccount.AccountHolderName?.split(' ').length >= 2 ? bankAccount.AccountHolderName?.split(' ')[1] : '',
      CompanyName:
        bankAccount.AccountHolderName?.split(' ').length < 2 ? bankAccount.AccountHolderName?.split(' ')[0] : '',
      SendEmail: shouldSendEmail,
      ReceiverEmailAddress: email,
      SenderEmailAddress: senderEmail,
      ClientRefNumber: bankAccount.ClientReferenceNumber,

      IntegrationType: bankAccount.IntegrationType,
      AccountNumber: bankAccount.AccountNumber,
      InstitutionNumber: bankAccount.FinancialInstitutionNumber,
      TransitNumber: bankAccount.BranchTransitNumber,

      FlinksAccountID: bankAccount.FlinksAccountID,
      FlinksLoginID: bankAccount.FlinksLoginID,
      PlaidAccountID: bankAccount.PlaidAccountID,
      PlaidProcessorToken: bankAccount.PlaidProcessorToken,
      InveriteRequestGUID: bankAccount.InveriteRequestGUID,
      MxAuthorizationCode: bankAccount.MxAuthorizationCode,
      FiservToken: bankAccount.FiservToken,
      MxProcessorToken: bankAccount.MxProcessorToken,
      FlinksAccountId: bankAccount.FlinksAccountID,
      FlinksLoginId: bankAccount.FlinksLoginID,

      ABARoutingNumber: bankAccount.ABARoutingNumber,

      Address1: bankAccount.Address,
      PhoneNumber: bankAccount.Phone,
      City: bankAccount.City,
      State: bankAccount['Province/State'],
      ZipCode: bankAccount['PostalCode/ZipCode'],

      Token: bankAccount.Token,
    });

    if (!verificationResponse.Success) {
      return {
        status: 'VERIFICATION_FAIL',
        message: verificationResponse.ErrorMessage,
      };
    }

    const link = verificationResponse.Link;

    if (shouldSendEmail) {
      return {
        status: 'VERIFICATION_SEND_SUCCESS',
        link,
      };
    }

    return {
      status: 'VERIFICATION_LINK_SUCCESS',
      link,
    };
  };

  const fetchInteracDetails = async (token: string) => {
    const res = await api.fetchInteracEmail({
      ClientAccountID: accountID,
    });

    if (!res.Success) {
      throw new Error(res.ErrorMessage);
    }

    return Object.values(res.InteracEmails).find((e) => e.Token === token);
  };

  const fetchCreditCardDetails = async (CreditCardToken: string) => {
    const res = await api.fetchCreditCards({
      CreditCardToken,
      ClientAccountID: accountID,
    });

    if (!res.Success) {
      throw new Error(res.ErrorMessage);
    }

    return res.CreditCards[0];
  };

  const fetchDebitCardDetails = async (DebitCardToken: string) => {
    const res = await api.fetchDebitCards({
      DebitCardToken,
      ClientAccountID: accountID,
    });

    if (!res.Success) {
      throw new Error(res.ErrorMessage);
    }

    return res.DebitCards[0];
  };

  return {
    fetchBankAccountDetails,
    fetchTransactionData,
    verifyBankAccount,
    verifyInteracAccount,
    verifyCardAccount,
    fetchInteracDetails,
    fetchCreditCardDetails,
    fetchDebitCardDetails,
    fetchBalanceData,
  };
}
